/* InnoPart custom select: progressive enhancement for <select data-ip-select>.
   The native select stays in the form (hidden) and keeps the submitted value,
   so the form still works without JavaScript. Keyboard: arrows, Home/End,
   Enter/Space, Escape, Tab and type-ahead. */
let uid = 0;

function enhance(select) {
  if (select.dataset.ipSelectReady) return;
  select.dataset.ipSelectReady = 'true';
  const id = `ip-select-${++uid}`;
  const label = select.id ? document.querySelector(`label[for="${select.id}"]`) : null;
  if (label && !label.id) label.id = `${id}-label`;

  const wrapper = document.createElement('div');
  wrapper.className = 'ip-select';
  const button = document.createElement('button');
  button.type = 'button';
  button.className = 'ip-select__button';
  button.setAttribute('aria-haspopup', 'listbox');
  button.setAttribute('aria-expanded', 'false');
  button.setAttribute('aria-controls', `${id}-list`);
  if (label) button.setAttribute('aria-labelledby', `${label.id} ${id}-value`);
  button.innerHTML = `<span class="ip-select__value" id="${id}-value"></span><span class="ip-select__chevron" aria-hidden="true"></span>`;
  const list = document.createElement('ul');
  list.className = 'ip-select__list';
  list.id = `${id}-list`;
  list.setAttribute('role', 'listbox');
  list.tabIndex = -1;
  list.hidden = true;
  if (label) list.setAttribute('aria-labelledby', label.id);

  const options = [...select.options].map((option, index) => {
    const item = document.createElement('li');
    item.className = 'ip-select__option';
    item.id = `${id}-option-${index}`;
    item.setAttribute('role', 'option');
    item.textContent = option.textContent;
    item.addEventListener('click', () => { choose(index); close(true); });
    item.addEventListener('mousemove', () => highlight(index));
    list.append(item);
    return item;
  });

  select.classList.add('ip-select__native');
  select.tabIndex = -1;
  select.setAttribute('aria-hidden', 'true');
  select.after(wrapper);
  wrapper.append(select, button, list);
  if (label) label.addEventListener('click', event => { event.preventDefault(); button.focus(); });

  let active = select.selectedIndex;
  let typed = '';
  let typedTimer;

  function sync() {
    const index = select.selectedIndex;
    button.querySelector('.ip-select__value').textContent = select.options[index]?.textContent ?? '';
    options.forEach((item, i) => item.setAttribute('aria-selected', String(i === index)));
  }
  function highlight(index) {
    active = Math.max(0, Math.min(options.length - 1, index));
    options.forEach((item, i) => item.classList.toggle('is-active', i === active));
    list.setAttribute('aria-activedescendant', options[active].id);
    options[active].scrollIntoView({ block: 'nearest' });
  }
  function choose(index) {
    if (select.selectedIndex !== index) {
      select.selectedIndex = index;
      select.dispatchEvent(new Event('change', { bubbles: true }));
    }
    sync();
  }
  function open() {
    if (!list.hidden) return;
    list.hidden = false;
    wrapper.classList.add('is-open');
    button.setAttribute('aria-expanded', 'true');
    highlight(select.selectedIndex);
    list.focus({ preventScroll: true });
  }
  function close(focusButton) {
    if (list.hidden) return;
    list.hidden = true;
    wrapper.classList.remove('is-open');
    button.setAttribute('aria-expanded', 'false');
    if (focusButton) button.focus();
  }
  function typeAhead(key) {
    clearTimeout(typedTimer);
    typed += key.toLowerCase();
    typedTimer = setTimeout(() => { typed = ''; }, 600);
    const start = list.hidden ? select.selectedIndex : active;
    const order = [...options.keys()].map(i => (start + 1 + i) % options.length);
    const match = order.find(i => options[i].textContent.toLowerCase().startsWith(typed));
    if (match === undefined) return;
    if (list.hidden) choose(match); else highlight(match);
  }

  button.addEventListener('click', () => (list.hidden ? open() : close(true)));
  button.addEventListener('keydown', event => {
    if (['ArrowDown', 'ArrowUp', 'Enter', ' '].includes(event.key)) { event.preventDefault(); open(); }
    else if (event.key.length === 1 && !event.metaKey && !event.ctrlKey) typeAhead(event.key);
  });
  list.addEventListener('keydown', event => {
    const keys = {
      ArrowDown: () => highlight(active + 1),
      ArrowUp: () => highlight(active - 1),
      Home: () => highlight(0),
      End: () => highlight(options.length - 1),
      Enter: () => { choose(active); close(true); },
      ' ': () => { choose(active); close(true); },
      Escape: () => close(true),
      Tab: () => { choose(active); close(false); }
    };
    if (keys[event.key]) {
      if (event.key !== 'Tab') event.preventDefault();
      keys[event.key]();
    } else if (event.key.length === 1 && !event.metaKey && !event.ctrlKey) typeAhead(event.key);
  });
  document.addEventListener('pointerdown', event => { if (!wrapper.contains(event.target)) close(false); });
  select.addEventListener('change', sync);
  select.form?.addEventListener('reset', () => setTimeout(sync));
  sync();
}

const run = root => root.querySelectorAll('select[data-ip-select]').forEach(enhance);
run(document);
document.addEventListener('shopify:section:load', event => run(event.target));
